import type { Editor } from '@tiptap/core';
import type { EditorState as ProseMirrorEditorState } from '@tiptap/pm/state';

import { Extension } from '@tiptap/core';
import { Plugin, PluginKey } from '@tiptap/pm/state';

import {
  normalizeHandle,
  type MessageMentionSuggestionItem,
} from './message-markdown-mention';

export interface MessageMentionCommandRange {
  from: number;
  to: number;
}

export interface MessageMentionMenuState {
  active: boolean;
  query: string;
  range: MessageMentionCommandRange | null;
  selectedIndex: number;
}

interface MessageMentionMenuStorage {
  items: MessageMentionSuggestionItem[];
}

interface MessageMentionTriggerMatch {
  from: number;
  query: string;
}

type MessageMentionMenuMeta =
  | { type: 'close' }
  | { type: 'setSelectedIndex'; index: number };

export const MESSAGE_MENTION_MENU_PLUGIN_KEY =
  new PluginKey<MessageMentionMenuState>('messageMentionMenu');

export const INACTIVE_MESSAGE_MENTION_MENU_STATE: MessageMentionMenuState = {
  active: false,
  query: '',
  range: null,
  selectedIndex: 0,
};

function getMessageMentionMenuStorage(
  editor: Editor,
): MessageMentionMenuStorage {
  const storage = editor.storage as unknown as {
    messageMentionMenu?: MessageMentionMenuStorage;
  };
  if (!storage.messageMentionMenu) {
    storage.messageMentionMenu = { items: [] };
  }

  return storage.messageMentionMenu;
}

export function findMessageMentionTrigger(
  textBeforeCursor: string,
): MessageMentionTriggerMatch | null {
  const match = /(?:^|\s)@([a-zA-Z0-9._-]*)$/.exec(textBeforeCursor);
  if (!match) {
    return null;
  }

  const atOffset = match.index + match[0].lastIndexOf('@');
  return {
    from: atOffset,
    query: match[1] ?? '',
  };
}

function isCodeContext(state: ProseMirrorEditorState): boolean {
  const marks = [
    ...state.selection.$from.marks(),
    ...(state.storedMarks ?? []),
  ];
  return (
    state.selection.$from.parent.type.spec.code === true ||
    marks.some((mark) => mark.type.spec.code === true)
  );
}

function getMessageMentionMenuMatch(
  state: ProseMirrorEditorState,
): { query: string; range: MessageMentionCommandRange } | null {
  if (!state.selection.empty) {
    return null;
  }

  const { $from, from } = state.selection;
  if (!$from.parent.isTextblock || isCodeContext(state)) {
    return null;
  }

  const textBeforeCursor = $from.parent.textBetween(
    0,
    $from.parentOffset,
    '\0',
    '\0',
  );
  const trigger = findMessageMentionTrigger(textBeforeCursor);
  if (!trigger) {
    return null;
  }

  const normalizedQuery = normalizeHandle(trigger.query);
  if (!normalizedQuery) {
    return null;
  }

  return {
    query: normalizedQuery,
    range: {
      from: from - (textBeforeCursor.length - trigger.from),
      to: from,
    },
  };
}

function clampSelectedIndex(index: number, length: number): number {
  if (length <= 0) {
    return 0;
  }

  return Math.min(Math.max(index, 0), length - 1);
}

export function closeMessageMentionMenu(editor: Editor) {
  editor.view.dispatch(
    editor.state.tr.setMeta(MESSAGE_MENTION_MENU_PLUGIN_KEY, {
      type: 'close',
    } satisfies MessageMentionMenuMeta),
  );
}

export function setMessageMentionMenuSelectedIndex(
  editor: Editor,
  index: number,
) {
  editor.view.dispatch(
    editor.state.tr.setMeta(MESSAGE_MENTION_MENU_PLUGIN_KEY, {
      type: 'setSelectedIndex',
      index,
    } satisfies MessageMentionMenuMeta),
  );
}

export function getMessageMentionMenuItems(
  editor: Editor,
): MessageMentionSuggestionItem[] {
  return getMessageMentionMenuStorage(editor).items;
}

export function setMessageMentionMenuItems(
  editor: Editor,
  items: MessageMentionSuggestionItem[],
) {
  getMessageMentionMenuStorage(editor).items = items;
}

export function insertMessageMention(
  editor: Editor,
  item: MessageMentionSuggestionItem,
  range: MessageMentionCommandRange,
) {
  const userId = item.userId.trim();
  const userName = normalizeHandle(item.userName);
  if (!userId || !userName) {
    return;
  }

  editor
    .chain()
    .focus()
    .deleteRange(range)
    .insertContent([
      {
        type: 'npAtHandle',
        attrs: { userId, userName },
      },
      {
        type: 'text',
        text: ' ',
      },
    ])
    .run();
}

export const MessageMentionMenuExtension = Extension.create({
  name: 'messageMentionMenu',

  addStorage() {
    return {
      items: [],
    } satisfies MessageMentionMenuStorage;
  },

  addProseMirrorPlugins() {
    const editor = this.editor;

    return [
      new Plugin<MessageMentionMenuState>({
        key: MESSAGE_MENTION_MENU_PLUGIN_KEY,
        state: {
          init: () => INACTIVE_MESSAGE_MENTION_MENU_STATE,
          apply: (transaction, pluginState, _oldState, newState) => {
            const meta = transaction.getMeta(
              MESSAGE_MENTION_MENU_PLUGIN_KEY,
            ) as MessageMentionMenuMeta | undefined;

            if (meta?.type === 'close') {
              return INACTIVE_MESSAGE_MENTION_MENU_STATE;
            }

            const match = getMessageMentionMenuMatch(newState);
            if (!match) {
              return INACTIVE_MESSAGE_MENTION_MENU_STATE;
            }

            const requestedIndex =
              meta?.type === 'setSelectedIndex'
                ? meta.index
                : pluginState.selectedIndex;

            const selectedIndex =
              pluginState.active &&
              pluginState.query === match.query &&
              pluginState.range?.from === match.range.from
                ? requestedIndex
                : 0;

            return {
              active: true,
              query: match.query,
              range: match.range,
              selectedIndex,
            };
          },
        },
        props: {
          handleKeyDown(view, event) {
            const menuState =
              MESSAGE_MENTION_MENU_PLUGIN_KEY.getState(view.state) ??
              INACTIVE_MESSAGE_MENTION_MENU_STATE;
            if (!menuState.active || !menuState.range) {
              return false;
            }

            const suggestions = getMessageMentionMenuItems(editor);
            if (suggestions.length === 0) {
              return false;
            }

            if (event.key === 'ArrowDown') {
              event.preventDefault();
              setMessageMentionMenuSelectedIndex(
                editor,
                (menuState.selectedIndex + 1) % suggestions.length,
              );
              return true;
            }

            if (event.key === 'ArrowUp') {
              event.preventDefault();
              setMessageMentionMenuSelectedIndex(
                editor,
                (menuState.selectedIndex - 1 + suggestions.length) %
                  suggestions.length,
              );
              return true;
            }

            if (event.key === 'Enter' || event.key === 'Tab') {
              event.preventDefault();
              insertMessageMention(
                editor,
                suggestions[
                  clampSelectedIndex(
                    menuState.selectedIndex,
                    suggestions.length,
                  )
                ] ?? suggestions[0],
                menuState.range,
              );
              return true;
            }

            if (event.key === 'Escape') {
              event.preventDefault();
              closeMessageMentionMenu(editor);
              return true;
            }

            return false;
          },
        },
      }),
    ];
  },
});
